/**
 * LayerOrderManager - Handles element z-index ordering
 */

import { EventEmitter } from '../core/EventEmitter';
import { EditableElement } from '../editing/types';
import {
  bringToFront,
  sendToBack,
  bringForward,
  sendBackward,
  getElementOrder,
  getElementZIndex,
  canMoveForward,
  canMoveBackward,
  sortByOrder,
} from '../editing/LayerOrder';

export class LayerOrderManager {
  private emitter: EventEmitter;
  
  // Callbacks
  private onOrderChange: ((elements: EditableElement[]) => void) | null = null;

  constructor(emitter: EventEmitter) {
    this.emitter = emitter;
  }

  /**
   * Set callback for order changes
   */
  setOnOrderChange(callback: (elements: EditableElement[]) => void): void {
    this.onOrderChange = callback;
  }

  /**
   * Bring elements to front
   */
  bringToFront(elements: EditableElement[], selectedIds: string[]): EditableElement[] {
    if (selectedIds.length === 0) return elements;
    
    const newElements = bringToFront(elements, selectedIds);
    
    if (this.onOrderChange) {
      this.onOrderChange(newElements);
    }
    
    return newElements;
  }

  /**
   * Send elements to back
   */
  sendToBack(elements: EditableElement[], selectedIds: string[]): EditableElement[] {
    if (selectedIds.length === 0) return elements;
    
    const newElements = sendToBack(elements, selectedIds);
    
    if (this.onOrderChange) {
      this.onOrderChange(newElements);
    }
    
    return newElements;
  }

  /**
   * Bring elements forward (one level)
   */
  bringForward(elements: EditableElement[], selectedIds: string[]): EditableElement[] {
    if (selectedIds.length === 0) return elements;
    
    const newElements = bringForward(elements, selectedIds);
    
    if (this.onOrderChange) {
      this.onOrderChange(newElements);
    }
    
    return newElements;
  }

  /**
   * Send elements backward (one level)
   */
  sendBackward(elements: EditableElement[], selectedIds: string[]): EditableElement[] {
    if (selectedIds.length === 0) return elements;
    
    const newElements = sendBackward(elements, selectedIds);
    
    if (this.onOrderChange) {
      this.onOrderChange(newElements);
    }
    
    return newElements;
  }

  /**
   * Get element order (array of element IDs)
   */
  getElementOrder(elements: EditableElement[]): string[] {
    return getElementOrder(elements);
  }

  /**
   * Get element z-index (position in array)
   */
  getElementZIndex(elements: EditableElement[], elementId: string): number {
    return getElementZIndex(elements, elementId);
  }

  /**
   * Check if element can move forward
   */
  canMoveForward(elements: EditableElement[], elementId: string): boolean {
    return canMoveForward(elements, elementId);
  }

  /**
   * Check if element can move backward
   */
  canMoveBackward(elements: EditableElement[], elementId: string): boolean {
    return canMoveBackward(elements, elementId);
  }

  /**
   * Sort elements by order
   */
  sortByOrder(elements: EditableElement[], order: string[]): EditableElement[] {
    return sortByOrder(elements, order);
  }

  /**
   * Destroy the layer order manager
   */
  destroy(): void {
    this.onOrderChange = null;
  }
}
